/**
 * ContributionsSection.js – Support at Home "Your Contributions" tab content.
 * Contribution rates, calculation breakdown, example service package, cashflow impact.
 * Used by SupportAtHomePage.js.
 */
import React, { useState, useEffect } from 'react';
import { ACAT_LEVEL_DATA } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import { ChevronDown, ChevronUp, Home, ShieldCheck, Users, Info, DollarSign, Lightbulb } from 'lucide-react';
import * as calculationApi from '../../services/calculationApi.ts';
import { IconTile, Badge, CalculationTooltip } from '../../components/ui/index.tsx';
import { LIFETIME_CAPS, yearsUntilCapReached } from '../../../../shared/src/calculation/supportAtHomeCalculations.ts';
import { DEEMING_RATE_LOWER, DEEMING_RATE_UPPER, DEEMING_THRESHOLDS } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

interface ContributionThresholds {
    assetMin: number;
    assetMax: number;
    incomeMin: number;
    incomeMax: number;
}

interface ContributionBudget {
    client: number;
    partner: number;
    total: number;
    distribution: { clinical: number; independence: number; everydayLiving: number };
}

interface ContributionRatesDetail {
    clinical: number;
    independence: number;
    everydayLiving: number;
    /** The no-worse-off ceiling on a year's contributions, in dollars — null when none applies. */
    annualCap?: number | null;
}

interface ContributionAmounts {
    clinical: number;
    independence: number;
    everydayLiving: number;
    totalAnnual: number;
}

/** calculateSupportAtHomeContribution result (server/src/rest/calculation/supportAtHomeContributionEngine.ts). */
interface ContributionBranch {
    pensionStatus: 'meansNotDisclosed' | 'fullPensioner' | 'partPensioner' | 'selfFunded';
    contributionReason?: string;
    nwopCategory?: string | null;
    meansDisclosed?: boolean;
    thresholds: ContributionThresholds;
    assessableIncome?: number;
    assessableAssets?: number;
    budget: ContributionBudget;
    contributionRates: ContributionRatesDetail;
    contributionAmounts: ContributionAmounts;
    lifetimeCap: number;
    lifetimeCapReached?: boolean;
    contributionAfterCap?: number;
    fortnightlyCap?: number;
    fortnightlyContribution?: number;
    participantType?: string;
    illnessSeparatedApplied?: boolean;
    /** True when the rates came from a Services Australia assessment-letter override on the fact-find. */
    ratesManuallyOverridden?: boolean;
    /** 'income' under the no-worse-off principle (assets play no part); 'incomeAndAssets' on the standard rates. */
    rateBasis?: 'income' | 'incomeAndAssets';
    /** True when the no-worse-off fortnightly cap (the previous income-tested care fee) limited this year's contribution. */
    nwopCapApplied?: boolean;
    /** What the year's contribution would have been without that cap. */
    uncappedAnnualContribution?: number;
    /** What the model says regardless of any override — for disclosing both. */
    modelledContributionRates?: ContributionRatesDetail;
}

/** calculateIndividualContributions result. */
interface IndividualContributions {
    client: ContributionBranch | null;
    partner: ContributionBranch | null;
    combined: ContributionBranch | null;
}

/** Calc-engine `financials` result bag — only the fields this section reads (see calculationApi.ts's
 * own `Financials = Record<string, unknown>`). */
interface FinancialsSnapshot {
    assessableIncomeFromPensions?: number;
    deemedIncome?: number;
    netRentalIncomeForPension?: number;
    otherAssessableIncome?: number;
    totalAssessableIncome?: number;
    assessableAssets?: number;
    cashFlow?: number;
    [key: string]: unknown;
}

export interface ContributionsSectionProps {
    factFind: DraftFactFind;
    /** Needed by the assessment-letter override, which lives ON the fact-find so it persists and
     * flows into every calculation. Optional so read-only render paths keep working. */
    setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    clientLevel?: { budget?: number | string | null; [key: string]: unknown } | null;
    onNext?: () => void;
    onBack?: () => void;
}

// Contributions Section
const ContributionsSection: React.FC<ContributionsSectionProps> = ({ factFind, setFactFind }) => {
    const isPartnered = factFind?.adviceFor === 'couple';

    // Get participant type from fact-find data
    const clientHcpStatus = factFind?.careNeeds?.clientAcat?.hcpBeforeSep2024 || 'no';
    const defaultParticipantType = clientHcpStatus === 'yes' ? 'nwop' : 'standard';
    const defaultNwopCategory = factFind?.careNeeds?.clientAcat?.hcpCategory || null;
    const defaultPreviousITCF = Number(factFind?.careNeeds?.clientAcat?.previousITCF) || 0;

    const [participantType] = useState(defaultParticipantType);
    const [nwopCategory] = useState(defaultNwopCategory);
    const [meansDisclosed] = useState(true);
    const [previousITCF] = useState(defaultPreviousITCF);
    const [showCalculationDetails, setShowCalculationDetails] = useState(false);
    const [showExampleServicePackage, setShowExampleServicePackage] = useState(false);

    // ── Assessment-letter override ────────────────────────────────────────────────────────────────
    // The client's REAL rates come from a Services Australia assessment, which can differ from the
    // model. The adviser can record the official rates; they live on the fact-find (so they persist
    // and flow into every calculation via the contribution engine) with who/when/source captured.
    const activeOverride = (factFind as { sahContributionRateOverride?: { independence?: number; everydayLiving?: number; source?: string; overriddenAt?: string; modelledIndependenceAtOverride?: number; modelledEverydayLivingAtOverride?: number } })?.sahContributionRateOverride;
    const [showOverrideForm, setShowOverrideForm] = useState(false);
    const [overrideIndependencePct, setOverrideIndependencePct] = useState('');
    const [overrideEverydayPct, setOverrideEverydayPct] = useState('');
    const [overrideSource, setOverrideSource] = useState('');
    const [overrideError, setOverrideError] = useState('');

    const saveOverride = () => {
        if (!setFactFind) return;
        const indep = parseFloat(overrideIndependencePct);
        const edl = parseFloat(overrideEverydayPct);
        if (!Number.isFinite(indep) || indep < 0 || indep > 100 || !Number.isFinite(edl) || edl < 0 || edl > 100) {
            setOverrideError('Enter both rates as percentages between 0 and 100.');
            return;
        }
        if (!overrideSource.trim()) {
            // Mandatory for compliance: an override without provenance is indistinguishable from a typo.
            setOverrideError('Record where these rates came from (e.g. "Services Australia assessment letter dated 12 August 2026").');
            return;
        }
        setOverrideError('');
        setFactFind((prev) => ({
            ...prev,
            sahContributionRateOverride: {
                independence: indep / 100,
                everydayLiving: edl / 100,
                source: overrideSource.trim(),
                overriddenAt: new Date().toISOString(),
                modelledIndependenceAtOverride: contributionData?.modelledContributionRates?.independence ?? contributionData?.contributionRates?.independence,
                modelledEverydayLivingAtOverride: contributionData?.modelledContributionRates?.everydayLiving ?? contributionData?.contributionRates?.everydayLiving,
                modelledPensionStatusAtOverride: contributionData?.pensionStatus,
            },
        }));
        setShowOverrideForm(false);
    };

    const removeOverride = () => {
        if (!setFactFind) return;
        setFactFind((prev) => {
            const next = { ...prev } as Record<string, unknown>;
            delete next.sahContributionRateOverride;
            return next as typeof prev;
        });
        setShowOverrideForm(false);
    };

    // Calculate financial position and contributions
    const [financials, setFinancials] = useState<FinancialsSnapshot | null>(null);
    useEffect(() => {
        let cancelled = false;
        calculationApi.calculateFinancials(factFind).then((r) => { if (!cancelled) setFinancials(r); });
        return () => { cancelled = true; };
    }, [factFind]);

    const [individualContributions, setIndividualContributions] = useState<IndividualContributions | null>(null);
    useEffect(() => {
        if (!financials) { setIndividualContributions(null); return undefined; }
        let cancelled = false;
        calculationApi.calculateIndividualContributions(factFind, financials, participantType, nwopCategory, meansDisclosed, previousITCF).then((r) => {
            if (!cancelled) setIndividualContributions(r);
        });
        return () => { cancelled = true; };
    }, [factFind, financials, participantType, nwopCategory, meansDisclosed, previousITCF]);

    // For display, use combined contribution for couples, client for singles
    const contributionData: ContributionBranch | null | undefined = individualContributions
        ? (isPartnered ? (individualContributions.combined || individualContributions.client) : individualContributions.client)
        : null;
    const hasAgedCareGoal = Boolean(factFind?.goals?.aged_care);
    const showIllnessSeparatedPreview = isPartnered && hasAgedCareGoal && !contributionData?.illnessSeparatedApplied;

    const [illnessSeparatedPreview, setIllnessSeparatedPreview] = useState<IndividualContributions | null>(null);
    useEffect(() => {
        if (!showIllnessSeparatedPreview || !financials) { setIllnessSeparatedPreview(null); return undefined; }
        let cancelled = false;
        calculationApi.calculateIndividualContributions(
            factFind,
            financials,
            participantType,
            nwopCategory,
            meansDisclosed,
            previousITCF,
            0.5,
            { forceIllnessSeparated: true }
        ).then((r) => { if (!cancelled) setIllnessSeparatedPreview(r); });
        return () => { cancelled = true; };
    }, [showIllnessSeparatedPreview, factFind, financials, participantType, nwopCategory, meansDisclosed, previousITCF]);
    const illnessSeparatedPreviewData = illnessSeparatedPreview
        ? (illnessSeparatedPreview.combined || illnessSeparatedPreview.client)
        : null;
    
    // Debug: Verify service contributions sum to total
    if (contributionData && contributionData.contributionAmounts) {
        const sumOfServices = (contributionData.contributionAmounts.clinical || 0) + 
                              (contributionData.contributionAmounts.independence || 0) + 
                              (contributionData.contributionAmounts.everydayLiving || 0);
        const total = contributionData.contributionAmounts.totalAnnual || 0;
        if (Math.abs(sumOfServices - total) > 1) { // Allow 1 cent rounding difference
            console.warn('âš ï¸ Service contributions do not sum to total:', {
                clinical: contributionData.contributionAmounts.clinical,
                independence: contributionData.contributionAmounts.independence,
                everydayLiving: contributionData.contributionAmounts.everydayLiving,
                sumOfServices,
                total,
                budgetTotal: contributionData.budget?.total
            });
        }
    }
    
    const formatCurrency = (value: number | null | undefined): string => {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
    };

    const formatPercent = (value: number | null | undefined): string => {
        return `${((value || 0) * 100).toFixed(1)}%`;
    };

    if (!financials || !contributionData) {
        return <div className="space-y-6 text-sm text-gray-500">Loading your contribution position…</div>;
    }

    // Calculate years until lifetime cap (simplified - assumes constant annual contribution)
    const yearsToCap = yearsUntilCapReached(contributionData.contributionAmounts.totalAnnual, 0, participantType);

    // Build personalized breakdowns for display
    const buildAssessableIncomeBreakdown = (): [string, number][] => {
        const breakdown: [string, number][] = [];
        if (financials.assessableIncomeFromPensions) breakdown.push(['Age Pension', financials.assessableIncomeFromPensions]);
        if (financials.deemedIncome) breakdown.push(['Deemed Investment Income', financials.deemedIncome]);
        if (financials.netRentalIncomeForPension) breakdown.push(['Net Rental Income', financials.netRentalIncomeForPension]);
        if (financials.otherAssessableIncome) breakdown.push(['Other Income', financials.otherAssessableIncome]);
        return breakdown;
    };

    const assessableIncomeBreakdown = buildAssessableIncomeBreakdown();
    
    return (
        <div className="space-y-6">
            {/* Participant Type Info - Read-only from fact-find */}
            {participantType === 'nwop' && (
                <div className="bg-white border border-slate-200/70 border-l-4 border-blue-500 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-800">
                            <p className="font-bold mb-1 text-blue-900">No Worse Off Principle Participant</p>
                            <p className="text-gray-700">
                                Based on your response in Step 3, you were in receipt of (or approved for) a Home Care Package on 12 September 2024.
                                Your contribution rates are calculated under the Department's 'no worse off' principle: nothing if you paid no
                                income-tested care fee under your package; otherwise a rate between 0% and 25% that depends on your <strong>income only</strong>
                                (assets play no part), and never more per fortnight than the income-tested care fee you paid before. The lifetime cap is the
                                package cap ({LIFETIME_CAPS.nwop.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}, indexed each March and September), and fees already paid under your package count towards it.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Pension tier for contributions — model estimate, not Centrelink */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                    <Info size={20} className="text-slate-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-800">
                        <p className="font-semibold text-gray-900 mb-1">How we set “full pension / part pension / self-funded” for your co-contribution estimate</p>
                        <p className="text-gray-700 leading-relaxed">
                            Services Australia does not send us your official pension outcome. This tool looks at the <strong>Age Pension amount calculated from your answers here</strong> and uses that to pick the right <strong>contribution-rate band</strong> (full pensioner, part pensioner, or self-funded). Your <strong>real</strong> home-care co-contribution is decided after government assessment — it may be different from what you see here.
                        </p>
                    </div>
                </div>
            </div>

            {/* Contribution Rates */}
            <div className="mb-6">
                <div className="mb-6">
                    <p className="text-gray-700 mb-4">
                        Based on your financial position, we have estimated the following <strong className="text-gray-900">Contribution Rates</strong>:
                    </p>
                    {showIllnessSeparatedPreview && illnessSeparatedPreviewData && (
                        <p className="text-xs text-gray-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                            If you are later assessed by Services Australia as an <strong>illness-separated couple</strong>, your estimated rates could change to:
                            {' '}Clinical <strong>{formatPercent(illnessSeparatedPreviewData.contributionRates.clinical)}</strong>,
                            {' '}Independence <strong>{formatPercent(illnessSeparatedPreviewData.contributionRates.independence)}</strong>,
                            {' '}Everyday Living <strong>{formatPercent(illnessSeparatedPreviewData.contributionRates.everydayLiving)}</strong>.
                        </p>
                    )}
                </div>
                
                {/* Service Category Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* Clinical Care */}
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                        <div className="flex items-start gap-3 mb-4">
                            <IconTile icon={ShieldCheck} tone="brand" variant="solid" size="md" />
                            <div>
                                <h5 className="font-bold text-gray-900">Clinical Care</h5>
                                <p className="text-xs text-gray-600">Nursing, physio, OT, personal care</p>
                            </div>
                        </div>
                        <div className="text-3xl font-bold text-blue-600 mb-2">
                            {formatPercent(contributionData.contributionRates.clinical)}
                        </div>
                        <div className="text-sm text-gray-700">
                            Contribution Rate
                        </div>
                    </div>
                    
                    {/* Independence Services */}
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                        <div className="flex items-start gap-3 mb-4">
                            <IconTile icon={Users} tone="emerald" variant="solid" size="md" />
                            <div>
                                <h5 className="font-bold text-gray-900">Independence</h5>
                                <p className="text-xs text-gray-600">Social support, transport, respite</p>
                            </div>
                        </div>
                        <div className="text-3xl font-bold text-green-600 mb-2">
                            {formatPercent(contributionData.contributionRates.independence)}
                        </div>
                        <div className="text-sm text-gray-700">
                            Contribution Rate
                        </div>
                    </div>
                    
                    {/* Everyday Living */}
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                        <div className="flex items-start gap-3 mb-4">
                            <IconTile icon={Home} tone="amber" variant="solid" size="md" />
                            <div>
                                <h5 className="font-bold text-gray-900">Everyday Living</h5>
                                <p className="text-xs text-gray-600">Cleaning, gardening, meals</p>
                            </div>
                        </div>
                        <div className="text-3xl font-bold text-orange-600 mb-2">
                            {formatPercent(contributionData.contributionRates.everydayLiving)}
                        </div>
                        <div className="text-sm text-gray-700">
                            Contribution Rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* How Your Contribution is Calculated - Collapsible */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <button
                    onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                    className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                    <span className="text-gray-700 font-medium">
                        Show how your contribution is calculated
                    </span>
                    <ChevronDown 
                        className={`w-5 h-5 text-gray-400 transition-transform ${showCalculationDetails ? 'rotate-180' : ''}`} 
                    />
                </button>
                
                {showCalculationDetails && (
                    <div className="px-6 pb-6 pt-4 border-t border-gray-200">
                        {/* Step 1: Assessable Income */}
                        <div className="mb-8 pb-8 border-b border-gray-200">
                            <h5 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-3">
                                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-navy text-white font-bold text-sm shadow-sm">1</span>
                                Your Assessable Income
                            </h5>
                            <div className="bg-gray-50 rounded-xl p-5 mb-3 border border-gray-200">
                                <div className="space-y-3">
                                    {assessableIncomeBreakdown.map(([label, amount], idx) => (
                                        <div key={idx} className="flex justify-between items-center text-sm">
                                            <span className="text-gray-600">{label}:</span>
                                            <span className="font-bold text-gray-900">{formatCurrency(amount)}</span>
                                        </div>
                                    ))}
                                    {(financials.totalAssessableIncome || 0) > 0 && (
                                        <div className="flex justify-between items-center text-base font-bold pt-3 border-t-2 border-gray-300 mt-3">
                                            <span className="text-gray-900">Total Assessable Income:</span>
                                            <span className="text-blue-600">{formatCurrency(financials.totalAssessableIncome)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-start gap-2 text-xs text-gray-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
                                <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
                                <span><strong className="text-gray-900">How deeming works:</strong> Financial investments are 'deemed' to earn income at set rates ({(DEEMING_RATE_LOWER * 100).toFixed(2)}% on the first {formatCurrency(DEEMING_THRESHOLDS.single)} for singles, {formatCurrency(DEEMING_THRESHOLDS.couple)} for pensioner couples, then {(DEEMING_RATE_UPPER * 100).toFixed(2)}% above). This deemed income is included in assessable income, even if your actual returns are different.</span>
                            </div>
                        </div>

                        {/* Step 2: Assessable Assets */}
                        <div className="mb-8 pb-8 border-b border-gray-200">
                            <h5 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-3">
                                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-navy text-white font-bold text-sm shadow-sm">2</span>
                                Your Assessable Assets
                            </h5>
                            <div className="bg-gray-50 rounded-xl p-5 mb-3 border border-gray-200">
                                <div className="text-sm mb-2">
                                    <div className="font-semibold text-gray-600 mb-2">{isPartnered ? 'Combined Household Assets:' : 'Total Assets:'}</div>
                                    <div className="text-3xl font-bold text-green-600">{formatCurrency(financials.assessableAssets)}</div>
                                </div>
                                {isPartnered && (
                                    <div className="text-xs text-gray-600 mb-2 bg-white rounded-lg p-3 border border-gray-300 mt-3">
                                        <strong className="text-gray-900">Individual Assessment:</strong> Combined household assets split 50/50 for individual contribution calculations
                                        <div className="mt-2 grid grid-cols-2 gap-3 text-gray-700">
                                            <div className="font-semibold">{factFind?.client?.firstName || 'Client'}: {formatCurrency((financials.assessableAssets || 0) / 2)}</div>
                                            <div className="font-semibold">{factFind?.partner?.firstName || 'Partner'}: {formatCurrency((financials.assessableAssets || 0) / 2)}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="text-xs text-gray-600 pt-3 border-t border-gray-300 mt-3">
                                    <strong className="text-gray-900">What's excluded:</strong> Your principal home, personal effects, funeral bonds, and prepaid funeral plans.
                                </div>
                            </div>
                            <div className="flex items-start gap-2 text-xs text-gray-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
                                <Info size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                                <span>Your contribution rate is based on <strong className="text-gray-900">whichever produces the higher rate</strong> - your assessable income or assessable assets position.</span>
                            </div>
                        </div>

                        {/* Step 3: Contribution Brackets */}
                        <div>
                            <h5 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-3">
                                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-navy text-white font-bold text-sm shadow-sm">3</span>
                                Your Contribution Rate Brackets
                            </h5>
                            <div className="bg-white rounded-xl p-5 border border-slate-200/70">
                                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-2 shadow-sm ${
                                    contributionData.pensionStatus === 'fullPensioner' 
                                        ? 'bg-green-500 text-white'
                                        : contributionData.pensionStatus === 'partPensioner'
                                        ? 'bg-yellow-500 text-white'
                                        : 'bg-orange-500 text-white'
                                }`}>
                                    Status: {contributionData.pensionStatus === 'fullPensioner' ? 'Full Pensioner' : 
                                            contributionData.pensionStatus === 'partPensioner' ? 'Part Pensioner / CSHC Holder' : 
                                            'Self-Funded Retiree'}
                                </div>
                                <p className="text-xs text-gray-600 mb-4">
                                    Label is based on your <strong>modelled</strong> pension in this tool, not on a Centrelink letter.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div className="bg-slate-50 rounded-xl p-4 shadow-sm border border-slate-200">
                                        <div className="font-semibold text-gray-600 mb-1 text-xs uppercase tracking-wide">Clinical Care</div>
                                        <div className="text-2xl font-bold text-blue-600">{formatPercent(contributionData.contributionRates.clinical)}</div>
                                    </div>
                                    <div className="bg-slate-50 rounded-xl p-4 shadow-sm border border-slate-200">
                                        <div className="font-semibold text-gray-600 mb-1 text-xs uppercase tracking-wide">Independence</div>
                                        <div className="text-2xl font-bold text-green-600">{formatPercent(contributionData.contributionRates.independence)}</div>
                                        {contributionData.ratesManuallyOverridden ? (
                                            <div className="text-xs mt-1">
                                                <span className="inline-block px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-semibold">From official assessment</span>
                                                <span className="block text-gray-500 mt-0.5">Modelled: {formatPercent(contributionData.modelledContributionRates?.independence)}</span>
                                            </div>
                                        ) : contributionData.participantType === 'nwop' ? (
                                            <div className="text-xs text-gray-500 mt-1">No worse off: 0–25%, income only</div>
                                        ) : contributionData.pensionStatus === 'partPensioner' && (
                                            <div className="text-xs text-gray-500 mt-1">Range: 5-50%</div>
                                        )}
                                    </div>
                                    <div className="bg-slate-50 rounded-xl p-4 shadow-sm border border-slate-200">
                                        <div className="font-semibold text-gray-600 mb-1 text-xs uppercase tracking-wide">Everyday Living</div>
                                        <div className="text-2xl font-bold text-orange-600">{formatPercent(contributionData.contributionRates.everydayLiving)}</div>
                                        {contributionData.ratesManuallyOverridden ? (
                                            <div className="text-xs mt-1">
                                                <span className="inline-block px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-semibold">From official assessment</span>
                                                <span className="block text-gray-500 mt-0.5">Modelled: {formatPercent(contributionData.modelledContributionRates?.everydayLiving)}</span>
                                            </div>
                                        ) : contributionData.participantType === 'nwop' ? (
                                            <div className="text-xs text-gray-500 mt-1">No worse off: 0–25%, income only</div>
                                        ) : contributionData.pensionStatus === 'partPensioner' && (
                                            <div className="text-xs text-gray-500 mt-1">Range: 17.5-80%</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Sliding Scale Explanation for Part Pensioners — the answer first, then
                                the three steps that produce it, in the client's own numbers. The rates
                                shown are ALWAYS the engine's (contributionData); the step arithmetic
                                reconstructs them for the reader, it never recomputes them for use. */}
                            {/* THE NO-WORSE-OFF WALKTHROUGH. A covered participant's rate has nothing to do with
                                assets and is capped at the fee they paid before, so the standard part-pension
                                walkthrough below (two bars, "whichever is further along") would explain a
                                calculation that is not theirs. This one explains theirs, in their numbers. */}
                            {contributionData.participantType === 'nwop' && !contributionData.ratesManuallyOverridden && (() => {
                                const nwopCategory = contributionData.nwopCategory === 'A' ? 'A' : 'B';
                                const income = Number(contributionData.assessableIncome ?? financials.totalAssessableIncome ?? 0);
                                const incomeMin = contributionData.thresholds.incomeMin;
                                const incomeMax = contributionData.thresholds.incomeMax;
                                const step1 = Math.round(Math.max(0, income - incomeMin) * 0.5);
                                const step2 = Math.round(Math.max(0, incomeMax - incomeMin) * 0.5);
                                const step3 = step2 > 0 ? Math.min(100, (step1 / step2) * 100) : 0;
                                const positionPct = Math.min(100, Math.max(0, ((income - incomeMin) / Math.max(1, incomeMax - incomeMin)) * 100));
                                const rate = contributionData.contributionRates.independence;
                                const annualCap = contributionData.contributionRates.annualCap;
                                const fortnightlyCap = annualCap != null ? annualCap / 26 : null;
                                const previousFeeDaily = fortnightlyCap != null ? fortnightlyCap / 14 : null;
                                return (
                                    <div className="mt-4 bg-white border border-slate-200/70 border-l-4 border-blue-500 rounded-lg p-4" data-testid="nwop-walkthrough">
                                        <div className="flex items-start gap-2 mb-2">
                                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm text-blue-900 flex-1 min-w-0">
                                                {nwopCategory === 'A' ? (
                                                    <>
                                                        <strong>Why you pay nothing.</strong> Under your Home Care Package you were assessed as not having to pay an
                                                        income-tested care fee. The Department's 'no worse off' principle keeps it that way for as long as you are in
                                                        Support at Home — even if you are reassessed into a higher classification. Your assets and income do not
                                                        change this.
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>How your 'no worse off' rate is worked out.</strong> Because you paid an income-tested care fee under
                                                        your package, you now pay a reduced rate that depends on your <strong>income only</strong> — your assets play no
                                                        part — and never more than the fee you paid before.
                                                        <div className="mt-3 space-y-3">
                                                            <div className="rounded-xl p-4 border text-xs shadow-sm bg-white border-blue-300 ring-1 ring-blue-200">
                                                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Step 1 — where your income sits</p>
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <strong className="text-gray-900">Your assessable income: {formatCurrency(income)}</strong>
                                                                    <span className="text-gray-600">{positionPct.toFixed(1)}% of the way from {formatCurrency(incomeMin)} to {formatCurrency(incomeMax)}</span>
                                                                </div>
                                                                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                                                                    <div className="h-2 rounded-full bg-blue-500" style={{ width: `${positionPct}%` }} />
                                                                </div>
                                                            </div>
                                                            <div className="bg-white rounded-xl p-4 border border-slate-200/70 border-l-4 border-amber-400 text-xs shadow-sm">
                                                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Step 2 — the arithmetic (Services Australia's method)</p>
                                                                {contributionData.pensionStatus === 'fullPensioner' ? (
                                                                    <p className="text-gray-800">As a full pensioner your 'no worse off' rate is <strong className="text-purple-700 text-sm">0%</strong> on both Independence and Everyday Living.</p>
                                                                ) : contributionData.pensionStatus === 'selfFunded' ? (
                                                                    <p className="text-gray-800">As a self-funded retiree your 'no worse off' rate is the maximum, <strong className="text-purple-700 text-sm">25%</strong> on both Independence and Everyday Living (the standard rates would be 50% and 80%).</p>
                                                                ) : (
                                                                    <div className="space-y-1 text-gray-800">
                                                                        <p>({formatCurrency(income)} − {formatCurrency(incomeMin)}) × 50% = <strong>{formatCurrency(step1)}</strong></p>
                                                                        <p>({formatCurrency(incomeMax)} − {formatCurrency(incomeMin)}) × 50% = <strong>{formatCurrency(step2)}</strong></p>
                                                                        <p>{formatCurrency(step1)} ÷ {formatCurrency(step2)} = {step3.toFixed(2)}% of the way → × 25% = <strong className="text-purple-700 text-sm">{formatPercent(rate)}</strong> on both Independence and Everyday Living</p>
                                                                    </div>
                                                                )}
                                                                <p className="text-gray-600 mt-2">Clinical Care is always 0% — fully government funded.</p>
                                                            </div>
                                                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-xs shadow-sm">
                                                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Step 3 — the ceiling</p>
                                                                {fortnightlyCap != null && previousFeeDaily != null ? (
                                                                    <p className="text-gray-800">
                                                                        You never pay more than your previous income-tested care fee: {formatCurrency(previousFeeDaily)} a day, so
                                                                        {' '}<strong>{formatCurrency(fortnightlyCap)} a fortnight</strong> ({formatCurrency(annualCap ?? 0)} a year).
                                                                        {contributionData.nwopCapApplied
                                                                            ? ` This year the ceiling applies: without it your contributions would be ${formatCurrency(contributionData.uncappedAnnualContribution ?? 0)}.`
                                                                            : ' Your contributions this year are below it.'}
                                                                    </p>
                                                                ) : (
                                                                    <p className="text-gray-800">
                                                                        You never pay more per fortnight than the income-tested care fee you paid before. Record that fee (per day) in
                                                                        Step 3 of your profile and this tool will apply the ceiling.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {contributionData.pensionStatus === 'partPensioner' && contributionData.participantType !== 'nwop' && (() => {
                                const pct = (value: number, min: number, max: number): number =>
                                    Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
                                const incomePct = pct(financials.totalAssessableIncome || 0, contributionData.thresholds.incomeMin, contributionData.thresholds.incomeMax);
                                const assetPct = pct(financials.assessableAssets || 0, contributionData.thresholds.assetMin, contributionData.thresholds.assetMax);
                                const assetsWin = assetPct >= incomePct;
                                const winnerPct = assetsWin ? assetPct : incomePct;
                                const winnerLabel = assetsWin ? 'assets' : 'income';

                                // Self-check: only show the band arithmetic when it actually reproduces
                                // the engine's rates (standard part-pensioner bands). NWOP categories and
                                // special cases use different bands — for them the formula would be a lie,
                                // so the step falls back to stating the modelled rates alone.
                                const reproduces = (minRate: number, maxRate: number, engineRate: number | null | undefined): boolean =>
                                    Math.abs((minRate + (winnerPct / 100) * (maxRate - minRate)) - (Number(engineRate) || 0)) < 0.0015;
                                const bandsMatch = reproduces(0.05, 0.50, contributionData.contributionRates.independence)
                                    && reproduces(0.175, 0.80, contributionData.contributionRates.everydayLiving);

                                const positionBar = (label: string, value: number, min: number, max: number, position: number, wins: boolean, barColor: string) => (
                                    <div className={`rounded-xl p-4 border text-xs shadow-sm ${wins ? 'bg-white border-blue-300 ring-1 ring-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <strong className="text-gray-900">{label}: {formatCurrency(value)}</strong>
                                            {wins && (
                                                <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold">sets your rate</span>
                                            )}
                                        </div>
                                        <div className="relative h-3 rounded-full bg-gray-200 overflow-hidden">
                                            <div className={`absolute inset-y-0 left-0 rounded-full ${barColor}`} style={{ width: `${Math.max(2, position)}%` }} />
                                        </div>
                                        <div className="flex justify-between mt-1 text-gray-500">
                                            <span>{formatCurrency(min)}<span className="block text-[10px]">full-pension threshold</span></span>
                                            <CalculationTooltip
                                                title={`${label} — how the position is calculated`}
                                                data={[
                                                    { label, value: formatCurrency(value) },
                                                    { label: 'Less the full-pension threshold', value: `− ${formatCurrency(min)}` },
                                                    { label: 'Your position in the band', value: formatCurrency(value - min) },
                                                    { label: 'Band width (self-funded − full-pension)', value: formatCurrency(max - min) },
                                                    { label: `${formatCurrency(Math.max(0, value - min))} ÷ ${formatCurrency(max - min)}`, value: `${position.toFixed(1)}% of the way`, emphasis: true },
                                                ]}
                                            >
                                                <span className="font-bold text-gray-800 border-b border-dotted border-gray-400 cursor-help">{position.toFixed(1)}% of the way</span>
                                            </CalculationTooltip>
                                            <span className="text-right">{formatCurrency(max)}<span className="block text-[10px]">self-funded threshold</span></span>
                                        </div>
                                    </div>
                                );

                                const formulaLine = (category: string, minRate: number, maxRate: number, engineRate: number | null | undefined) => (
                                    <p className="text-gray-800 mb-1.5">
                                        <strong>{category}</strong> runs from {formatPercent(minRate)} (full pensioner) to {formatPercent(maxRate)} (self-funded).
                                        You&apos;re {winnerPct.toFixed(1)}% of the way, so:{' '}
                                        <span className="whitespace-nowrap">{formatPercent(minRate)} + {winnerPct.toFixed(1)}% × {formatPercent(maxRate - minRate)}</span>
                                        {' '}= <strong className="text-purple-700 text-sm">{formatPercent(engineRate)}</strong>
                                    </p>
                                );

                                return (
                                    <div className="mt-4 bg-white border border-slate-200/70 border-l-4 border-blue-500 rounded-lg p-4">
                                        <div className="flex items-start gap-2 mb-2">
                                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm text-blue-900 flex-1 min-w-0">
                                                <strong>How your part-pension rates are worked out.</strong> As a part pensioner your rates sit between the full-pensioner minimum and the self-funded maximum. Three steps decide exactly where:
                                                <div className="mt-3 space-y-3">
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Step 1 — where you sit in the part-pension band</p>
                                                        <div className="space-y-2">
                                                            {positionBar('Your income', financials.totalAssessableIncome || 0, contributionData.thresholds.incomeMin, contributionData.thresholds.incomeMax, incomePct, !assetsWin, 'bg-blue-500')}
                                                            {positionBar('Your assets', financials.assessableAssets || 0, contributionData.thresholds.assetMin, contributionData.thresholds.assetMax, assetPct, assetsWin, 'bg-green-500')}
                                                        </div>
                                                    </div>

                                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-xs shadow-sm">
                                                        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Step 2 — which one counts</p>
                                                        <p className="text-gray-800">
                                                            Whichever position is further along sets your rate — for you, that&apos;s your <strong>{winnerLabel}</strong> ({winnerPct.toFixed(1)}% of the way).
                                                            {' '}That also means strategies that reduce your assessable <strong>{winnerLabel}</strong> are the ones that can lower these rates.
                                                        </p>
                                                    </div>

                                                    <div className="bg-white rounded-xl p-4 border border-slate-200/70 border-l-4 border-amber-400 text-xs shadow-sm">
                                                        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">Step 3 — the arithmetic</p>
                                                        {bandsMatch ? (
                                                            <>
                                                                {formulaLine('Independence', 0.05, 0.50, contributionData.contributionRates.independence)}
                                                                {formulaLine('Everyday Living', 0.175, 0.80, contributionData.contributionRates.everydayLiving)}
                                                            </>
                                                        ) : (
                                                            <p className="text-gray-800 mb-1.5">
                                                                Applying your position to your contribution bands gives{' '}
                                                                <strong className="text-purple-700 text-sm">{formatPercent(contributionData.contributionRates.independence)}</strong> for Independence and{' '}
                                                                <strong className="text-purple-700 text-sm">{formatPercent(contributionData.contributionRates.everydayLiving)}</strong> for Everyday Living.
                                                            </p>
                                                        )}
                                                        <p className="text-gray-600 mt-2">Clinical Care is always {formatPercent(contributionData.contributionRates.clinical)} — fully government funded at every means level.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Assessment-letter override — record the OFFICIAL rates when Services
                                Australia's letter differs from the model. Lives on the fact-find so
                                it persists and flows into every calculation; provenance is mandatory. */}
                            {setFactFind && (
                                <div className="mt-4 bg-white border border-slate-200/70 border-l-4 border-purple-400 rounded-lg p-4 text-sm">
                                    {activeOverride && contributionData.ratesManuallyOverridden ? (
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold">Rates manually overridden</span>
                                            </div>
                                            <p className="text-gray-800 mb-1">
                                                Using official rates: <strong>{formatPercent(activeOverride.independence)}</strong> Independence,{' '}
                                                <strong>{formatPercent(activeOverride.everydayLiving)}</strong> Everyday Living
                                                {' '}(modelled at the time: {formatPercent(activeOverride.modelledIndependenceAtOverride)} / {formatPercent(activeOverride.modelledEverydayLivingAtOverride)}).
                                            </p>
                                            <p className="text-xs text-gray-600 mb-2">
                                                Source: {activeOverride.source || '—'} · Recorded {activeOverride.overriddenAt ? new Date(activeOverride.overriddenAt).toLocaleDateString('en-AU') : '—'}
                                            </p>
                                            <p className="text-xs text-gray-600 mb-3">
                                                These official rates are used everywhere — the budget, both modelling modes and the
                                                suggestion engine. If your finances change materially, Services Australia may
                                                reassess; update or remove the override when a new letter arrives.
                                            </p>
                                            <button type="button" onClick={removeOverride} className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                                                Remove override (back to modelled rates)
                                            </button>
                                        </div>
                                    ) : showOverrideForm ? (
                                        <div>
                                            <p className="font-semibold text-gray-900 mb-2">Enter the rates from your assessment letter</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                                <div>
                                                    <label htmlFor="ov-indep" className="block text-xs font-semibold text-gray-700 mb-1">Independence rate (%)</label>
                                                    <input id="ov-indep" type="number" min="0" max="100" step="0.1" value={overrideIndependencePct} onChange={(e) => setOverrideIndependencePct(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder={`Modelled: ${formatPercent(contributionData.contributionRates.independence)}`} />
                                                </div>
                                                <div>
                                                    <label htmlFor="ov-edl" className="block text-xs font-semibold text-gray-700 mb-1">Everyday Living rate (%)</label>
                                                    <input id="ov-edl" type="number" min="0" max="100" step="0.1" value={overrideEverydayPct} onChange={(e) => setOverrideEverydayPct(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder={`Modelled: ${formatPercent(contributionData.contributionRates.everydayLiving)}`} />
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="ov-source" className="block text-xs font-semibold text-gray-700 mb-1">Where did these rates come from? (required)</label>
                                                <input id="ov-source" type="text" value={overrideSource} onChange={(e) => setOverrideSource(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder='e.g. "Services Australia assessment letter dated 12 August 2026"' />
                                            </div>
                                            <p className="text-xs text-gray-600 mb-2">
                                                Clinical Care stays 0% — it is fully government funded for everyone. The date, source
                                                and the modelled rates at this moment are recorded with the override for compliance.
                                            </p>
                                            {overrideError && <p className="text-xs text-red-600 mb-2">{overrideError}</p>}
                                            <div className="flex gap-2">
                                                <button type="button" onClick={saveOverride} className="px-3 py-1.5 text-xs font-semibold rounded-md bg-purple-600 text-white hover:bg-purple-700">Use these official rates</button>
                                                <button type="button" onClick={() => { setShowOverrideForm(false); setOverrideError(''); }} className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-start justify-between gap-3">
                                            <p className="text-gray-700 text-xs">
                                                Been assessed by Services Australia? If your letter shows different rates, you can
                                                use the official figures instead of these modelled ones.
                                            </p>
                                            <button type="button" onClick={() => setShowOverrideForm(true)} className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md border border-purple-300 text-purple-700 hover:bg-purple-50">
                                                Enter official rates
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Example Service Package - Collapsible */}
            <div className="mb-6">
                <button
                    onClick={() => setShowExampleServicePackage(!showExampleServicePackage)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <Info className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700">Example Service Package (Click to {showExampleServicePackage ? 'hide' : 'view'})</span>
                    </div>
                    {showExampleServicePackage ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                </button>
                
                {showExampleServicePackage && (
                    <div className="mt-4 bg-white/60 backdrop-blur-xl border border-white rounded-2xl shadow-lg shadow-gray-200/50 p-6">
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Badge tone="warning">EXAMPLE ONLY</Badge>
                                <p className="text-xs text-gray-600 italic">
                                    This is a hypothetical example. Your actual service package and contributions will be determined by your individual needs and financial position.
                                </p>
                            </div>
                            <div className="bg-slate-50 border-l-4 border-blue-400 rounded-lg p-4">
                                {(() => {
                                    const clientAcatLevel = factFind?.careNeeds?.clientAcat?.homeCareLevel;
                                    const levelName = (clientAcatLevel && ACAT_LEVEL_DATA[clientAcatLevel]?.label) || 'Support at Home - Classification 6';
                                    
                                    if (isPartnered && contributionData.budget.client > 0 && contributionData.budget.partner > 0) {
                                        return (
                                            <p className="text-sm text-gray-700">
                                                Based on an assumed service distribution whereby you require <strong>40% Clinical Care, 35% Independence Services and 25% Everyday Living</strong>, 
                                                this is how much you would pay based on a <strong>combined service package of {formatCurrency(contributionData.budget.total)} per year</strong> ({factFind?.client?.firstName || 'John'} <strong>{formatCurrency(contributionData.budget.client)}</strong>, {factFind?.partner?.firstName || 'Kim'} <strong>{formatCurrency(contributionData.budget.partner)}</strong>).
                                            </p>
                                        );
                                    } else {
                                        return (
                                            <p className="text-sm text-gray-700">
                                                Based on an assumed service distribution whereby you require <strong>40% Clinical Care, 35% Independence Services, and 25% Everyday Living</strong>, 
                                                this is how much you would pay based on your <strong>{levelName}</strong> which has a total funding package of <strong>{formatCurrency(contributionData.budget.total)}</strong> per year.
                                            </p>
                                        );
                                    }
                                })()}
                            </div>
                        </div>
                        
                        {/* Service Category Contributions - Subtle Cards */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            {/* Clinical Care */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="text-xs font-medium text-gray-600 mb-1">Clinical</div>
                                <div className="text-xl font-bold text-gray-900">
                                    {formatCurrency(contributionData.contributionAmounts.clinical)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {formatPercent(contributionData.contributionRates.clinical)} rate
                                </div>
                            </div>
                            
                            {/* Independence Services */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="text-xs font-medium text-gray-600 mb-1">Independence</div>
                                <div className="text-xl font-bold text-gray-900">
                                    {formatCurrency(contributionData.contributionAmounts.independence)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {formatPercent(contributionData.contributionRates.independence)} rate
                                </div>
                            </div>
                            
                            {/* Everyday Living */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="text-xs font-medium text-gray-600 mb-1">Everyday Living</div>
                                <div className="text-xl font-bold text-gray-900">
                                    {formatCurrency(contributionData.contributionAmounts.everydayLiving)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {formatPercent(contributionData.contributionRates.everydayLiving)} rate
                                </div>
                            </div>
                        </div>
                        
                        {/* Total Contribution - Subtle */}
                        <div className="bg-gray-50 border border-gray-300 rounded-lg p-5 mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-base font-semibold text-gray-700">Total Annual Contribution:</span>
                                <span className="text-3xl font-bold text-gray-900">
                                    {formatCurrency(contributionData.contributionAmounts.totalAnnual)}
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 text-right">
                                {formatCurrency(contributionData.contributionAmounts.totalAnnual / 12)} per month
                            </div>
                            {contributionData.nwopCapApplied && (
                                <p className="mt-2 text-xs text-blue-900 bg-white border border-blue-200 rounded-lg px-3 py-2">
                                    Capped at your previous income-tested care fee ({formatCurrency((contributionData.contributionRates.annualCap ?? 0) / 26)} a fortnight) under the
                                    'no worse off' principle. Without the cap this year's contributions would be {formatCurrency(contributionData.uncappedAnnualContribution ?? 0)}.
                                </p>
                            )}
                        </div>

                        {/* Cashflow Impact - Inside Example Section */}
                        <div className="bg-white/50 rounded-lg p-5 border border-gray-200">
                            <h4 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-gray-600" />
                                Impact on Your {isPartnered ? 'Household' : ''} Annual Cashflow
                            </h4>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <span className="text-sm font-medium text-gray-700">Your Current Annual Cashflow:</span>
                                    <span className="text-lg font-bold text-gray-900">{formatCurrency(financials.cashFlow || 0)}</span>
                                </div>
                                <div className="flex justify-between items-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <span className="text-sm font-medium text-gray-700">Less: Support at Home Contribution:</span>
                                    <span className="text-lg font-bold text-red-600">-{formatCurrency(contributionData.contributionAmounts.totalAnnual)}</span>
                                </div>
                                <div className="pt-3 border-t-2 border-gray-300 bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-semibold text-gray-900">Your Net Annual Cashflow:</span>
                                        <span className={`text-xl font-bold ${
                                            (financials.cashFlow || 0) - contributionData.contributionAmounts.totalAnnual >= 0 
                                                ? 'text-green-600' 
                                                : 'text-red-600'
                                        }`}>
                                            {formatCurrency((financials.cashFlow || 0) - contributionData.contributionAmounts.totalAnnual)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-3 pt-3 border-t border-gray-200">
                                        {isPartnered 
                                            ? "This assumes the combined household example service package is paid from your current household income sources."
                                            : "This assumes the example service package is paid from your current income sources."
                                        }
                                        {contributionData.contributionAmounts.totalAnnual > (financials.cashFlow || 0) && (
                                            <span className="block mt-2 text-red-600 font-medium">âš ï¸ Your cashflow may need adjustment to cover these costs.</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Lifetime Cap Information - Inside Example Section */}
                        {yearsToCap && !isPartnered && (
                            <div className="mt-4 bg-white/50 rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-gray-700">Lifetime Cap:</span>
                                    <span className="text-xl font-bold text-gray-900">{formatCurrency(contributionData.lifetimeCap)}</span>
                                </div>
                                <div className="text-xs text-gray-600">
                                    At this contribution level, you would reach the lifetime cap in approximately <strong className="text-gray-900">{yearsToCap} {yearsToCap === 1 ? 'year' : 'years'}</strong>, after which you'd pay no more fees for Support at Home services.
                                </div>
                            </div>
                        )}
                        
                        {yearsToCap && isPartnered && (
                            <div className="mt-4 bg-white/50 rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-gray-700">Lifetime Cap (per person):</span>
                                    <span className="text-xl font-bold text-gray-900">{formatCurrency(contributionData.lifetimeCap)}</span>
                                </div>
                                <div className="text-xs text-gray-600">
                                    Combined household cap: <strong className="text-gray-900">{formatCurrency(contributionData.lifetimeCap * 2)}</strong> – At this combined contribution level, you would reach the lifetime cap in approximately <strong className="text-gray-900">{yearsToCap} {yearsToCap === 1 ? 'year' : 'years'}</strong>, after which you'd pay no more fees for Support at Home services.
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* Next Steps Message */}
            <div className="bg-white border border-slate-200/70 border-l-4 border-blue-500 rounded-lg p-5 shadow-sm">
                <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                        {/* Points at the Service Builder, which now follows this screen. The rates
                            shown above are percentages; what they cost depends on the services the
                            client actually needs, so the next question is what those are — not yet
                            how to lower the rates (owner direction, 2026-08-27: need before money). */}
                        <p className="font-bold text-blue-900 mb-2">Next: what care do you actually need?</p>
                        <p className="text-sm text-gray-800">
                            The rates above are percentages — what they cost depends on the services in your plan. Click{' '}
                            <strong>&quot;Next&quot;</strong> to build the service plan, and you will see the real dollars
                            rather than an illustrative package. After that, <strong>Optimise Your Plan</strong> looks at
                            whether those rates themselves can be lowered: some clients see real reductions through careful
                            planning, others find their rates cannot be moved much.
                        </p>
                    </div>
                </div>
            </div>
            
        </div>
    );
};

export default ContributionsSection;

